import supabase from '../lib/supabase';

// Table names with unique suffixes to avoid conflicts
const TABLES = {
  CUSTOMERS: 'customers_auto_crm_7a9b3c2d1e',
  LEADS: 'leads_auto_crm_7a9b3c2d1e',
  VEHICLES: 'vehicles_auto_crm_7a9b3c2d1e',
  SALES: 'sales_auto_crm_7a9b3c2d1e',
  INTERACTIONS: 'interactions_auto_crm_7a9b3c2d1e',
  SMS_MESSAGES: 'sms_messages_7a9b3c2d1e'
};

// Customers
export const customersService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Supabase error fetching customers:', error);
        throw error;
      }
      return data?.map(this.transformCustomer) || [];
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  },
  
  async create(customer) {
    try {
      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .insert([{
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          city: customer.city,
          state: customer.state,
          zip_code: customer.zipCode,
          interested_vehicle: customer.interestedVehicle,
          budget: customer.budget,
          status: customer.status,
          lead_source: customer.leadSource,
          notes: customer.notes,
          tags: customer.tags || []
        }])
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error creating customer:', error);
        throw error;
      }
      return this.transformCustomer(data);
    } catch (error) {
      console.error('Error creating customer:', error);
      // Return a fallback customer with client-side generated ID
      return {
        id: `cust-${Date.now()}`,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zipCode: customer.zipCode,
        interestedVehicle: customer.interestedVehicle,
        budget: customer.budget,
        status: customer.status,
        leadSource: customer.leadSource,
        notes: customer.notes,
        tags: customer.tags || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastContact: null
      };
    }
  },
  
  async update(id, customer) {
    try {
      const { data, error } = await supabase
        .from(TABLES.CUSTOMERS)
        .update({
          first_name: customer.firstName,
          last_name: customer.lastName,
          email: customer.email,
          phone: customer.phone,
          address: customer.address,
          city: customer.city,
          state: customer.state,
          zip_code: customer.zipCode,
          interested_vehicle: customer.interestedVehicle,
          budget: customer.budget,
          status: customer.status,
          lead_source: customer.leadSource,
          notes: customer.notes,
          tags: customer.tags || [],
          last_contact: customer.lastContact,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Supabase error updating customer:', error);
        throw error;
      }
      return this.transformCustomer(data);
    } catch (error) {
      console.error('Error updating customer:', error);
      // Return the updated customer with the same ID
      return {
        id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zipCode: customer.zipCode,
        interestedVehicle: customer.interestedVehicle,
        budget: customer.budget,
        status: customer.status,
        leadSource: customer.leadSource,
        notes: customer.notes,
        tags: customer.tags || [],
        updatedAt: new Date().toISOString(),
        lastContact: customer.lastContact
      };
    }
  },
  
  async delete(id) {
    try {
      const { error } = await supabase
        .from(TABLES.CUSTOMERS)
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error('Supabase error deleting customer:', error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Error deleting customer:', error);
      throw error;
    }
  },
  
  transformCustomer(dbCustomer) {
    if (!dbCustomer) return null;
    
    return {
      id: dbCustomer.id,
      firstName: dbCustomer.first_name,
      lastName: dbCustomer.last_name,
      email: dbCustomer.email,
      phone: dbCustomer.phone,
      address: dbCustomer.address,
      city: dbCustomer.city,
      state: dbCustomer.state,
      zipCode: dbCustomer.zip_code,
      interestedVehicle: dbCustomer.interested_vehicle,
      budget: dbCustomer.budget,
      status: dbCustomer.status,
      leadSource: dbCustomer.lead_source,
      notes: dbCustomer.notes,
      tags: dbCustomer.tags || [],
      createdAt: dbCustomer.created_at,
      updatedAt: dbCustomer.updated_at,
      lastContact: dbCustomer.last_contact
    };
  }
};

// Leads
export const leadsService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.LEADS)
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching leads:', error);
        throw error;
      }
      return data?.map(this.transformLead) || [];
    } catch (error) {
      console.error('Error fetching leads:', error);
      return [];
    }
  },
  
  async create(lead) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LEADS)
        .insert([{
          first_name: lead.firstName,
          last_name: lead.lastName,
          email: lead.email,
          phone: lead.phone,
          interested_vehicle: lead.interestedVehicle,
          budget: lead.budget,
          status: lead.status,
          priority: lead.priority,
          source: lead.source,
          follow_up_date: lead.followUpDate,
          notes: lead.notes,
          tags: lead.tags || []
        }])
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error creating lead:', error);
        throw error;
      }
      return this.transformLead(data);
    } catch (error) {
      console.error('Error creating lead:', error);
      // Return a fallback lead with client-side generated ID
      return {
        id: `lead-${Date.now()}`,
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        interestedVehicle: lead.interestedVehicle,
        budget: lead.budget,
        status: lead.status,
        priority: lead.priority,
        source: lead.source,
        followUpDate: lead.followUpDate,
        notes: lead.notes,
        tags: lead.tags || [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  },
  
  async update(id, lead) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LEADS)
        .update({
          first_name: lead.firstName,
          last_name: lead.lastName,
          email: lead.email,
          phone: lead.phone,
          interested_vehicle: lead.interestedVehicle,
          budget: lead.budget,
          status: lead.status,
          priority: lead.priority,
          source: lead.source,
          follow_up_date: lead.followUpDate,
          notes: lead.notes,
          tags: lead.tags || [],
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error updating lead:', error);
        throw error;
      }
      return this.transformLead(data);
    } catch (error) {
      console.error('Error updating lead:', error);
      // Return the updated lead with the same ID
      return {
        id,
        firstName: lead.firstName,
        lastName: lead.lastName,
        email: lead.email,
        phone: lead.phone,
        interestedVehicle: lead.interestedVehicle,
        budget: lead.budget,
        status: lead.status,
        priority: lead.priority,
        source: lead.source,
        followUpDate: lead.followUpDate,
        notes: lead.notes,
        tags: lead.tags || [],
        updatedAt: new Date().toISOString()
      };
    }
  },
  
  async delete(id) {
    try {
      const { error } = await supabase
        .from(TABLES.LEADS)
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error('Supabase error deleting lead:', error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Error deleting lead:', error);
      throw error;
    }
  },
  
  transformLead(dbLead) {
    if (!dbLead) return null;
    
    return {
      id: dbLead.id,
      firstName: dbLead.first_name,
      lastName: dbLead.last_name,
      email: dbLead.email,
      phone: dbLead.phone,
      interestedVehicle: dbLead.interested_vehicle,
      budget: dbLead.budget,
      status: dbLead.status,
      priority: dbLead.priority,
      source: dbLead.source,
      followUpDate: dbLead.follow_up_date,
      notes: dbLead.notes,
      tags: dbLead.tags || [],
      createdAt: dbLead.created_at,
      updatedAt: dbLead.updated_at
    };
  }
};

// Vehicles
export const vehiclesService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching vehicles:', error);
        throw error;
      }
      return data?.map(this.transformVehicle) || [];
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      return [];
    }
  },
  
  async create(vehicle) {
    try {
      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .insert([{
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          type: vehicle.type,
          vin: vehicle.vin,
          color: vehicle.color,
          mileage: vehicle.mileage,
          price: vehicle.price,
          status: vehicle.status,
          description: vehicle.description,
          image_url: vehicle.imageUrl
        }])
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error creating vehicle:', error);
        throw error;
      }
      return this.transformVehicle(data);
    } catch (error) {
      console.error('Error creating vehicle:', error);
      // Return a fallback vehicle with client-side generated ID
      return {
        id: `veh-${Date.now()}`,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        type: vehicle.type,
        vin: vehicle.vin,
        color: vehicle.color,
        mileage: vehicle.mileage,
        price: vehicle.price,
        status: vehicle.status,
        description: vehicle.description,
        imageUrl: vehicle.imageUrl,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    }
  },
  
  async update(id, vehicle) {
    try {
      const { data, error } = await supabase
        .from(TABLES.VEHICLES)
        .update({
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          type: vehicle.type,
          vin: vehicle.vin,
          color: vehicle.color,
          mileage: vehicle.mileage,
          price: vehicle.price,
          status: vehicle.status,
          description: vehicle.description,
          image_url: vehicle.imageUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error updating vehicle:', error);
        throw error;
      }
      return this.transformVehicle(data);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      // Return the updated vehicle with the same ID
      return {
        id,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        type: vehicle.type,
        vin: vehicle.vin,
        color: vehicle.color,
        mileage: vehicle.mileage,
        price: vehicle.price,
        status: vehicle.status,
        description: vehicle.description,
        imageUrl: vehicle.imageUrl,
        updatedAt: new Date().toISOString()
      };
    }
  },
  
  async delete(id) {
    try {
      const { error } = await supabase
        .from(TABLES.VEHICLES)
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error('Supabase error deleting vehicle:', error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  },
  
  transformVehicle(dbVehicle) {
    if (!dbVehicle) return null;
    
    return {
      id: dbVehicle.id,
      make: dbVehicle.make,
      model: dbVehicle.model,
      year: dbVehicle.year,
      type: dbVehicle.type,
      vin: dbVehicle.vin,
      color: dbVehicle.color,
      mileage: dbVehicle.mileage,
      price: dbVehicle.price,
      status: dbVehicle.status,
      description: dbVehicle.description,
      imageUrl: dbVehicle.image_url,
      createdAt: dbVehicle.created_at,
      updatedAt: dbVehicle.updated_at
    };
  }
};

// Sales
export const salesService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.SALES)
        .select('*')
        .order('sale_date', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching sales:', error);
        throw error;
      }
      return data?.map(this.transformSale) || [];
    } catch (error) {
      console.error('Error fetching sales:', error);
      return [];
    }
  },
  
  async create(sale) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SALES)
        .insert([{
          customer_id: sale.customerId,
          vehicle_id: sale.vehicleId,
          customer_name: sale.customerName,
          vehicle_details: sale.vehicleDetails,
          sale_price: sale.salePrice,
          sale_date: sale.saleDate,
          status: sale.status,
          notes: sale.notes
        }])
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error creating sale:', error);
        throw error;
      }
      
      // Update vehicle status to sold if vehicleId is provided
      if (sale.vehicleId) {
        await supabase
          .from(TABLES.VEHICLES)
          .update({ status: 'sold' })
          .eq('id', sale.vehicleId);
      }
      
      return this.transformSale(data);
    } catch (error) {
      console.error('Error creating sale:', error);
      // Return a fallback sale with client-side generated ID
      return {
        id: `sale-${Date.now()}`,
        customerId: sale.customerId,
        vehicleId: sale.vehicleId,
        customerName: sale.customerName,
        vehicleDetails: sale.vehicleDetails,
        salePrice: sale.salePrice,
        saleDate: sale.saleDate,
        status: sale.status,
        notes: sale.notes,
        createdAt: new Date().toISOString()
      };
    }
  },
  
  async update(id, sale) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SALES)
        .update({
          customer_id: sale.customerId,
          vehicle_id: sale.vehicleId,
          customer_name: sale.customerName,
          vehicle_details: sale.vehicleDetails,
          sale_price: sale.salePrice,
          sale_date: sale.saleDate,
          status: sale.status,
          notes: sale.notes
        })
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error updating sale:', error);
        throw error;
      }
      return this.transformSale(data);
    } catch (error) {
      console.error('Error updating sale:', error);
      // Return the updated sale with the same ID
      return {
        id,
        customerId: sale.customerId,
        vehicleId: sale.vehicleId,
        customerName: sale.customerName,
        vehicleDetails: sale.vehicleDetails,
        salePrice: sale.salePrice,
        saleDate: sale.saleDate,
        status: sale.status,
        notes: sale.notes
      };
    }
  },
  
  async delete(id) {
    try {
      const { error } = await supabase
        .from(TABLES.SALES)
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error('Supabase error deleting sale:', error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error;
    }
  },
  
  transformSale(dbSale) {
    if (!dbSale) return null;
    
    return {
      id: dbSale.id,
      customerId: dbSale.customer_id,
      vehicleId: dbSale.vehicle_id,
      customerName: dbSale.customer_name,
      vehicleDetails: dbSale.vehicle_details,
      salePrice: parseFloat(dbSale.sale_price),
      saleDate: dbSale.sale_date,
      status: dbSale.status,
      notes: dbSale.notes,
      createdAt: dbSale.created_at
    };
  }
};

// Interactions
export const interactionsService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.INTERACTIONS)
        .select('*')
        .order('timestamp', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching interactions:', error);
        throw error;
      }
      return data?.map(this.transformInteraction) || [];
    } catch (error) {
      console.error('Error fetching interactions:', error);
      return [];
    }
  },
  
  async getByCustomer(customerId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.INTERACTIONS)
        .select('*')
        .eq('customer_id', customerId)
        .order('timestamp', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching customer interactions:', error);
        throw error;
      }
      return data?.map(this.transformInteraction) || [];
    } catch (error) {
      console.error('Error fetching customer interactions:', error);
      return [];
    }
  },
  
  async getByLead(leadId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.INTERACTIONS)
        .select('*')
        .eq('lead_id', leadId)
        .order('timestamp', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching lead interactions:', error);
        throw error;
      }
      return data?.map(this.transformInteraction) || [];
    } catch (error) {
      console.error('Error fetching lead interactions:', error);
      return [];
    }
  },
  
  async create(interaction) {
    try {
      const timestamp = interaction.timestamp || new Date().toISOString();
      
      const { data, error } = await supabase
        .from(TABLES.INTERACTIONS)
        .insert([{
          customer_id: interaction.customerId,
          lead_id: interaction.leadId,
          type: interaction.type,
          notes: interaction.notes,
          timestamp: timestamp
        }])
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error creating interaction:', error);
        throw error;
      }
      
      // Update last_contact for customer if applicable
      if (interaction.customerId) {
        await supabase
          .from(TABLES.CUSTOMERS)
          .update({ last_contact: timestamp })
          .eq('id', interaction.customerId);
      }
      
      return this.transformInteraction(data);
    } catch (error) {
      console.error('Error creating interaction:', error);
      // Return a fallback interaction with client-side generated ID
      return {
        id: `int-${Date.now()}`,
        customerId: interaction.customerId,
        leadId: interaction.leadId,
        type: interaction.type,
        notes: interaction.notes,
        timestamp: interaction.timestamp || new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
    }
  },
  
  async delete(id) {
    try {
      const { error } = await supabase
        .from(TABLES.INTERACTIONS)
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error('Supabase error deleting interaction:', error);
        throw error;
      }
      return true;
    } catch (error) {
      console.error('Error deleting interaction:', error);
      throw error;
    }
  },
  
  transformInteraction(dbInteraction) {
    if (!dbInteraction) return null;
    
    return {
      id: dbInteraction.id,
      customerId: dbInteraction.customer_id,
      leadId: dbInteraction.lead_id,
      type: dbInteraction.type,
      notes: dbInteraction.notes,
      timestamp: dbInteraction.timestamp,
      createdAt: dbInteraction.created_at
    };
  }
};

// SMS Messages
export const smsService = {
  async getAll() {
    try {
      const { data, error } = await supabase
        .from(TABLES.SMS_MESSAGES)
        .select('*')
        .order('timestamp', { ascending: false });
        
      if (error) {
        console.error('Supabase error fetching SMS messages:', error);
        throw error;
      }
      return data || [];
    } catch (error) {
      console.error('Error fetching SMS messages:', error);
      return [];
    }
  },
  
  async create(message) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SMS_MESSAGES)
        .insert([{
          to_number: message.to,
          from_number: message.from,
          body: message.body,
          status: message.status,
          sid: message.sid,
          customer_id: message.customerId,
          lead_id: message.leadId
        }])
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error saving SMS message:', error);
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error saving SMS message:', error);
      // Return a fallback message
      return {
        id: `sms-${Date.now()}`,
        to: message.to,
        from: message.from,
        body: message.body,
        status: message.status,
        sid: message.sid,
        timestamp: new Date().toISOString()
      };
    }
  },
  
  async updateStatus(sid, status) {
    try {
      const { data, error } = await supabase
        .from(TABLES.SMS_MESSAGES)
        .update({ status: status })
        .eq('sid', sid)
        .select()
        .single();
        
      if (error) {
        console.error('Supabase error updating SMS message status:', error);
        throw error;
      }
      return data;
    } catch (error) {
      console.error('Error updating SMS message status:', error);
      return null;
    }
  }
};